<script setup>
import { useForm } from '@inertiajs/vue3';
import { IconTriangleFilled, IconTriangleInvertedFilled } from '@tabler/icons-vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormSection from '@/components/FormSection.vue';
import FormError from '@/components/Forms/FormError.vue';

const props = defineProps({
    widgets: {
        type: Array,
        required: true,
    },
    url: {
        type: String,
        required: true,
    },
});

const form = useForm({
    widgets: props.widgets,
});

const submit = () => {
    form.transform((data) => {
        data.widgets = data.widgets
            .filter((s) => s.is_selected)
            .map((w, index) => {
                return {
                    widget_id: w.value,
                    order: index,
                };
            });
        return data;
    }).patch(props.url, {
        preserveScroll: true,
    });
};

const swapItems = (a, i, j) => {
    const newArray = [...a];
    newArray[i] = a[j];
    newArray[j] = a[i];
    return newArray;
};

const moveUp = (index) => {
    form.widgets = swapItems(form.widgets, index, index - 1);
};
const moveDown = (index) => {
    form.widgets = swapItems(form.widgets, index, index + 1);
};
</script>

<template>
    <FormSection>
        <template v-slot:title>
            {{ $t('messages.widgets') }}
        </template>
        <template v-slot:content>
            <form action="" @submit.prevent="submit">
                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            <span class="text-gray-700">#</span>
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.name') }}
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $t('messages.description') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody v-auto-animate>
                                    <tr
                                        v-for="(widget, index) in form.widgets"
                                        :class="[
                                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                                            { 'opacity-75': !widget.is_selected },
                                        ]"
                                        :key="widget.value"
                                    >
                                        <td
                                            class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex gap-2 items-center"
                                        >
                                            <span class="tabular-nums">{{ index + 1 }}</span>
                                            <input
                                                :id="`widget_${widget.value}`"
                                                name="widget"
                                                type="checkbox"
                                                v-model="widget.is_selected"
                                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <div class="flex gap-1 items-center">
                                                <button
                                                    @click="moveUp(index)"
                                                    :disabled="index === 0"
                                                    class="disabled:text-gray-200 text-gray-400"
                                                    type="button"
                                                >
                                                    <IconTriangleFilled class="w-3 h-3" />
                                                </button>
                                                <button
                                                    @click="moveDown(index)"
                                                    :disabled="index === form.widgets.length - 1"
                                                    class="disabled:text-gray-200 text-gray-400"
                                                    type="button"
                                                >
                                                    <IconTriangleInvertedFilled class="w-3 h-3" />
                                                </button>
                                            </div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-600">
                                            {{ widget.text }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap leading-5 font-medium text-gray-900">
                                            <p class="text-gray-400 max-w-lg whitespace-pre-wrap text-xs">
                                                {{ widget.description }}
                                            </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="mt-1">
                    <FormError :value="form.errors.widgets" class="mt-2" />
                </div>

                <div
                    class="flex items-center justify-end px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md"
                >
                    <div class="mt-1">
                        <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                    </div>
                </div>
            </form>
        </template>
    </FormSection>
</template>

<script>
export default {
    name: 'DashboardWidgetSettingsForm',
};
</script>
